<template>
  <SoRadioCards v-if="isLoaded" v-model="value">
    <div class="d-flex flex-column gap-4 w-75">
      <SoRadioCard
        v-for="slot in formattedSlots"
        :key="slot.value"
        :value="slot.value"
        data-testid="appointment-slot-card"
      >
        <template #title>{{ slot.title }}</template>
        <span class="slot-description">{{ slot.description }}</span>
      </SoRadioCard>
      <SoRadioCard
        value="NO_SUITABLE_SLOTS"
        data-testid="no-suitable-slots-card"
      >
        <template #title>Collect customer availability</template>
        I have offered all available slots and none of them work for the
        customer
      </SoRadioCard>
    </div>
  </SoRadioCards>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import dayjs from "dayjs"
  import SoRadioCards from "@/components/SoRadioCards.vue"
  import SoRadioCard from "@/components/SoRadioCard.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { ITimeSlot } from "@/models/imbpAvailableSlots/imbpAvailableSlots.types"
  import { formatFullDateLong, formatTimeSlot } from "@/helpers/dateHelper"

  interface FormattedSlot {
    value: string
    title: string
    description: string
  }

  export default defineComponent({
    components: {
      SoRadioCards,
      SoRadioCard,
    },
    props: {
      modelValue: {
        type: Object,
        default: () => ({}),
      },
      appointmentSlots: {
        type: Object as PropType<ITimeSlot[]>,
        required: true,
      },
    },
    emits: ["update:model-value"],
    data() {
      return {
        current: useCurrentAccountStore(),
        value: null as string | null,
        isLoaded: false,
      }
    },
    computed: {
      formattedSlots(): FormattedSlot[] {
        return this.appointmentSlots.map((slot, index) => {
          const [startDate, endDate] = slot.dateTimeRange
          const title = formatFullDateLong(dayjs(startDate))
          const description = formatTimeSlot(dayjs(startDate), dayjs(endDate))
          return {
            value: `slot-${index}`,
            title,
            description,
          }
        })
      },
    },
    watch: {
      appointmentSlots: {
        immediate: true,
        handler(newValue) {
          if (Array.isArray(newValue)) {
            this.isLoaded = true
          }
        },
      },
      value: {
        handler(newValue) {
          if (newValue) {
            this.$emit("update:model-value", this.getSelectedSlotData(newValue))
          }
        },
      },
    },
    unmounted() {
      this.current.getImbpSlotWithCoreId = false
    },
    methods: {
      getSelectedSlotData(value: string) {
        if (value === "NO_SUITABLE_SLOTS") {
          return {
            appointmentSlot: {} as ITimeSlot,
            noSuitableAppointments: true,
          }
        }

        const index = Number.parseInt(value.replace("slot-", ""), 10)

        return {
          appointmentSlot: this.appointmentSlots[index] || ({} as ITimeSlot),
          noSuitableAppointments: false,
        }
      },
    },
  })
</script>

<style scoped>
  .slot-description {
    font-size: 18px;
  }
</style>
